<template>
    <div class="flex flex-col gap-4 mt-4">
        <div>
            <vs-button v-if="!showAddForm" class="rounded-lg" @click="showAddForm = true">
                {{ $t('admin.resorts.integrations.add') }}
            </vs-button>

            <div v-if="showAddForm" class="vx-card p-8">
                <h4 class="font-semibold text-base mb-4">{{ $t('admin.resorts.integrations.new_integration') }}</h4>

                <LabelForm font-size="text-sm" margins="mt-0">{{ $t('admin.resorts.integrations.select_type') }}</LabelForm>
                <Combobox
                    :condensedOptions="true"
                    v-model="newIntegrationTypeKey"
                    class="mt-2 max-w-xs"
                    :options="availableTypes.map(t => ({ key: t.key, text: t.name })).sort((a, b) => a.text.localeCompare(b.text))"
                />

                <div v-if="newIntegrationTypeKey && newForm.template.length > 0" class="mt-4">
                    <DynamicForm :value="newForm" @input="newForm = $event" />
                </div>

                <div class="flex gap-4 mt-6">
                    <vs-button
                        class="rounded-lg"
                        :disabled="!newIntegrationTypeKey"
                        @click="saveNewIntegration"
                    >
                        {{ $t('resorts.touristic-objects.form.saveButton') }}
                    </vs-button>
                    <vs-button type="border" class="rounded-lg" @click="cancelAddForm">
                        {{ $t('resorts.touristic-objects.form.cancelButton') }}
                    </vs-button>
                </div>
            </div>
        </div>

        <div>
            <p v-if="!loading && integrations.length === 0" class="text-grey">
                {{ $t('admin.resorts.integrations.empty') }}
            </p>

            <SKCollapse accordion class="vx-card">
                <SKCollapseItem
                    v-for="integration in integrations"
                    :key="integration.uuid"
                >
                    <template #header>
                        <span class="font-semibold">{{ getIntegrationTypeName(integration.integration_type_key) }}</span>
                        <vs-button
                            type="border"
                            color="danger"
                            size="small"
                            class="rounded-lg ml-4"
                            @click.stop="showDeleteConfirmation(integration)"
                        >
                            <feather-icon icon="TrashIcon" svgClasses="w-4 h-4" />
                        </vs-button>
                    </template>

                    <template #content>
                        <DynamicForm
                            v-if="editForms[integration.uuid] && editForms[integration.uuid].template.length > 0"
                            :value="editForms[integration.uuid]"
                            @input="onEditFormInput(integration.uuid, $event)"
                        />
                        <p v-else class="text-grey text-sm">
                            {{ $t('admin.resorts.integrations.no_configuration') }}
                        </p>
                        <div class="flex gap-4 mt-4">
                            <vs-button class="rounded-lg" @click="saveIntegration(integration)">
                                {{ $t('resorts.touristic-objects.form.saveButton') }}
                            </vs-button>
                        </div>
                    </template>
                </SKCollapseItem>
            </SKCollapse>
        </div>
    </div>
</template>

<script>
import LabelForm from "@/modules/Shared/Components/form/LabelForm.vue";
import Combobox from "@/modules/Shared/Components/Combobox.vue";
import DynamicForm from "@/modules/Shared/Components/form/DynamicForm.vue";
import SKCollapse from "@/modules/Shared/Components/collapse/SKCollapse.vue";
import SKCollapseItem from "@/modules/Shared/Components/collapse/SKCollapseItem.vue";
import IntegrationService from "@/modules/Admin/Services/IntegrationService";
import loader from "@/modules/Shared/Mixins/loader";

export default {
    name: "ResortIntegrations",
    components: { DynamicForm, Combobox, LabelForm, SKCollapse, SKCollapseItem },
    mixins: [loader],
    props: {
        resortUuid: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            integrationTypes: [],
            integrations: [],
            editForms: {},
            showAddForm: false,
            newIntegrationTypeKey: null,
            newForm: { template: [], values: {} },
        }
    },
    computed: {
        availableTypes() {
            const usedKeys = new Set(this.integrations.map(i => i.integration_type_key))
            return this.integrationTypes.filter(t => !usedKeys.has(t.key))
        }
    },
    watch: {
        newIntegrationTypeKey(key) {
            if (!key) {
                this.newForm = { template: [], values: {} }
                return
            }
            const type = this.integrationTypes.find(t => t.key === key)
            this.newForm = {
                template: this.normalizeTemplate(type ? type.configuration_template : []),
                values: {}
            }
        }
    },
    async created() {
        await this.load(async () => {
            const [types, integrations] = await Promise.all([
                IntegrationService.listIntegrationTypes(),
                IntegrationService.listIntegrations(this.resortUuid)
            ])
            this.integrationTypes = types
            this.integrations = integrations
            this.initEditForms(integrations)
        })
    },
    methods: {
        normalizeTemplate(template) {
            return template.map(field => ({
                ...field,
                template: field.fields || []
            }))
        },
        configToFormValues(normalizedTemplate, configuration) {
            const result = { ...(configuration || {}) }
            normalizedTemplate.forEach(field => {
                if (field.input === 'tags' && Array.isArray(result[field.key])) {
                    result[field.key] = result[field.key].join(',')
                }
            })
            return result
        },
        formValuesToConfig(normalizedTemplate, formValues) {
            const result = { ...(formValues || {}) }
            normalizedTemplate.forEach(field => {
                if (field.input === 'tags') {
                    const val = result[field.key]
                    result[field.key] = val ? val.split(',') : []
                }
            })
            return result
        },
        initEditForms(integrations) {
            const forms = {}
            integrations.forEach(integration => {
                const type = this.integrationTypes.find(t => t.key === integration.integration_type_key)
                const normalizedTemplate = this.normalizeTemplate(type ? type.configuration_template : [])
                forms[integration.uuid] = {
                    template: normalizedTemplate,
                    values: this.configToFormValues(normalizedTemplate, integration.configuration)
                }
            })
            this.editForms = forms
        },
        onEditFormInput(uuid, form) {
            this.$set(this.editForms, uuid, form)
        },
        getIntegrationTypeName(key) {
            const type = this.integrationTypes.find(t => t.key === key)
            return type ? type.name : key
        },
        async saveIntegration(integration) {
            await this.loadAndNotify(async () => {
                const form = this.editForms[integration.uuid]
                const configuration = this.formValuesToConfig(form.template, form.values)
                await IntegrationService.updateIntegration(
                    this.resortUuid,
                    integration.uuid,
                    { configuration }
                )
                await this.reloadIntegrations()
            })
        },
        showDeleteConfirmation(integration) {
            this.$vs.dialog({
                type: 'confirm',
                color: 'danger',
                title: this.$t('admin.resorts.integrations.delete.title'),
                text: this.$t('admin.resorts.integrations.delete.confirm'),
                acceptText: this.$t('shops.delete'),
                accept: () => this.confirmDelete(integration.uuid)
            })
        },
        async confirmDelete(integrationUuid) {
            await this.loadAndNotify(async () => {
                await IntegrationService.deleteIntegration(this.resortUuid, integrationUuid)
                await this.reloadIntegrations()
            })
        },
        async saveNewIntegration() {
            await this.loadAndNotify(async () => {
                const integrationUuid = crypto.randomUUID()
                const configuration = this.formValuesToConfig(this.newForm.template, this.newForm.values)
                await IntegrationService.createIntegration(
                    this.resortUuid,
                    integrationUuid,
                    {
                        integration_type_key: this.newIntegrationTypeKey,
                        configuration
                    }
                )
                this.cancelAddForm()
                await this.reloadIntegrations()
            })
        },
        async reloadIntegrations() {
            this.integrations = await IntegrationService.listIntegrations(this.resortUuid)
            this.initEditForms(this.integrations)
        },
        cancelAddForm() {
            this.showAddForm = false
            this.newIntegrationTypeKey = null
            this.newForm = { template: [], values: {} }
        }
    }
}
</script>
